<template>
  <div>
    <b-form-group
      class="mt-1"
      :class="{ 'border border-danger rounded p-1': isFailed }"
      label-for="boolInput"
    >
      <label
        v-if="type != 'statement'"
        v-html="name"
      />
  
      <div class="d-flex justify-content-start align-items-center">
        <div>
          <!-- Boolean -->
          <div v-if="type === 'bool'" class="d-flex justify-content-start align-items-center">
            <b-form-radio-group
              v-model="answer"
              :options="options"
              :disabled="true"
              @change="answerChanged"
            />
          </div>
      
          <!-- Rating -->
          <div v-if="type === 'rating'" class="d-flex justify-content-start align-items-center">
            <div class="rating-container position-relative">
              <b-form-rating
                v-model="answer"
                size="lg"
                inline="inline"
                no-border="no-border"
                variant="warning"
                :disabled="true"
                class="ml-1"
                @change="answerChanged"
                @mouseover.native="handleStarHover"
                @mouseleave.native="hideTooltip"
              />
              <!-- Tooltip personalizado -->
              <div
                v-if="showTooltip"
                class="custom-tooltip"
                :style="tooltipStyle"
              >
                {{ tooltipText }}
              </div>
            </div>

            <InformationButton class="mr-1"/>

            <strong :style="{ fontSize: '1.15rem' }" class="mr-1">
              {{ answer ? answer : 'N/A' }}
            </strong>
          </div>
      
          <!-- Text -->
          <b-form-textarea
            v-if="type === 'text'"
            v-model="answer"
            rows="3"
            :disabled="true"
            @change="answerChanged"
          />
      
          <!-- Statement -->
          <div v-if="type === 'statement'">
            <b-card
              v-html="showSeeMoreButton && showShortStatement ? shortStatement : name"
              class="mb-0"
            />
            <b-button
              v-if="showSeeMoreButton"
              size="sm"
              :variant="showShortStatement ? 'info' : 'warning'"
              @click="showShortStatement = !showShortStatement"
            >
              {{ showShortStatement ? $t('message.see_more') : $t('message.see_less') }}
            </b-button>
          </div>
        </div>
  
        <!-- Buttons -->
        <div class="d-flex justify-content-start align-items-center">
          <!-- Document Repository -->
          <document-repository
            :documents="[]"
            :keys="behaviour.evidenceKey || []"
            :read-only="true"
            type="evidence"
            size="sm"
            buttonExtraText="evidences"
          />

          <!-- See case generated for this behaviour -->
          <b-button
            v-if="behaviour.caseId"
            class="ml-1"
            size="sm"
            variant="success"
            @click="openCase(behaviour.caseId)"
          >
            {{ $t('message.execute_case') }}
          </b-button>
      
          <!-- Add case for failed behaviours only -->
          <b-button
            v-else-if="isFailed"
            class="ml-1"
            size="sm"
            :variant="'info'"
            @click="isImprovementHandlerSidebarActive = true"
          >
            {{ $t('add_improvement') }}
          </b-button>
        </div>
      </div>

      <!-- Default text field for comments -->
      <b-form-input
        v-if="comment"
        v-model="comment"
        class="mt-1"
        :disabled="true"
      />
    </b-form-group>

    <!-- Improvement sidebar -->
    <todo-task-handler-sidebar
      v-model="isImprovementHandlerSidebarActive"
      :task="improvement"
      :clear-task-data="clearImprovementData"
      @add-task="addImprovement"
    />
  </div>
</template>

<script>
import { ref } from '@vue/composition-api'
import { BFormGroup, BFormRadioGroup, BFormRating, BFormTextarea } from 'bootstrap-vue'
import i18n from '@/libs/i18n'
import { useRouter } from '@core/utils/utils'
import useCommon from '@/views/organization/useCommon'
import TodoTaskHandlerSidebar from '@/views/apps/todo/TodoTaskHandlerSidebar.vue'
import store from "@/store"
import useNotifications from '@/composables/useNotifications'
import { ratingLabels } from '@/constants'
import InformationButton from '@/views/components/Shared/Buttons/InformationButton.vue'
import { useLambda } from '@/views/habit/awsLambda'
import DocumentRepository from '@/views/habit/DocumentRepository'
import { oid, date } from '@/helpers/mongoHelpers'

export default {
  components: {
    BFormGroup,
    BFormRadioGroup,
    BFormRating,
    BFormTextarea,
    TodoTaskHandlerSidebar,
    InformationButton,
    DocumentRepository,
  },

  props: {
    behaviour: {
      type: Object,
      required: true,
    },
    activityName: {
      type: String,
      required: true,
    },
    confirmationId: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      name: this.behaviour.name,
      type: this.behaviour.type,
      answer: this.behaviour.answer,
      comment: this.behaviour.comment,
      expectedAnswer: this.behaviour.expectedAnswer,
      showTooltip: false,
      tooltipText: '',
      tooltipStyle: {},
    }
  },

  computed: {
    shortStatement() {
      if (this.type === 'statement') {
        return this.name.slice(0, 100) + "..."
      }
    },
    showSeeMoreButton() {
      if (this.type === 'statement' && this.name?.length > 100) return true
      return false
    },
    isFailed() {
      return (
        (this.type === 'bool' && ((this.answer === 'true' && !this.expectedAnswer) || (this.answer === 'false' && this.expectedAnswer))) ||
        (this.type === 'rating' && ['1', '2', '3', '4'].includes(this.answer))
      )
    }
  },

  methods: {
    answerChanged(answer) {
      this.$emit('answered', answer)
    },
    handleStarHover(event) {
      // Encontrar el elemento estrella más cercano
      const star = event.target.closest('.b-rating-star')
      if (!star) return

      // Obtener el índice de la estrella basado en su posición
      const stars = star.parentElement.querySelectorAll('.b-rating-star')
      const starIndex = Array.from(stars).indexOf(star)

      // Configurar el tooltip
      this.tooltipText = ratingLabels.value[starIndex] || ''
      
      if (this.tooltipText) {
        const ratingContainer = this.$el.querySelector('.rating-container')
        const containerRect = ratingContainer.getBoundingClientRect()
        const starRect = star.getBoundingClientRect()
        
        // Calcular posición relativa al contenedor
        const relativeLeft = starRect.left - containerRect.left + (starRect.width / 2)
        
        this.tooltipStyle = {
          position: 'absolute',
          top: '-30px',
          left: `${relativeLeft}px`,
          transform: 'translateX(-50%)',
          zIndex: 1000
        }
        this.showTooltip = true
      }
    },
    hideTooltip() {
      this.showTooltip = false
    }
  },

  setup(props, { emit }) {
    const userData = store.state?.userStore?.userData
    const userId = userData.worker_id?.$oid
    const clientId = userData.role !== "admin" ? userData.client.$oid : null
    const { openDocument, createCase, formatDate } = useCommon()
    const { showSuccessMessage, showErrorMessage } = useNotifications()
    const { router } = useRouter()
    const showShortStatement = ref(true)
    const now = new Date()

    const options = [
      { text: i18n.t('message.yes'), value: 'true' },
      { text: 'No', value: 'false' },
      { text: 'N/A', value: 'unknown' },
    ]

    const openCase = (id) => {
      const routeData = router.resolve({ name: 'organization-case-edit', params: { id } });
      window.open(routeData.href, '_blank');
    }

    // Improvement sidebar
    const isImprovementHandlerSidebarActive = ref(false)

    const blankImprovement = {
      client_id: { link: clientId },
      note: '',
      assignee: '',
      createdAt: now,
      dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
      activity: props.activityName,
      behaviour: {
        name: props.behaviour.name,
        type: props.behaviour.type,
      },
      confirmation: props.confirmationId,
      // objective: '',
    }

    const improvement = ref(JSON.parse(JSON.stringify(blankImprovement)))

    const clearImprovementData = () => {
      improvement.value = JSON.parse(JSON.stringify(blankImprovement))
    }

    const addImprovement = async (improvementData) => {
      emit('isCreatingCase', true)

      try {
        improvementData.client_id = oid(improvementData.client_id.link)
        improvementData.assignee = oid(improvementData.assignee)
        improvementData.confirmation = improvementData.confirmation ? oid(improvementData.confirmation) : null
        improvementData.proposalResponsible = oid(improvementData.assignee)
        improvementData.approvalResponsible = improvementData.approvalResponsible ? oid(improvementData.approvalResponsible) : null
        improvementData.verificationResponsible = improvementData.verificationResponsible ? oid(improvementData.verificationResponsible) : null
        improvementData.dueDate = improvementData.dueDate ? date(formatDate(improvementData.dueDate)) : null
        improvementData.createdAt = improvementData.createdAt ? date(formatDate(improvementData.createdAt)) : null
        improvementData.proposalDueDate = improvementData.proposalDueDate ? date(formatDate(improvementData.proposalDueDate)) : null
        improvementData.approvalDueDate = improvementData.approvalDueDate ? date(formatDate(improvementData.approvalDueDate)) : null
        improvementData.verificationDueDate = improvementData.verificationDueDate ? date(formatDate(improvementData.verificationDueDate)) : null
        improvementData.associatedCases = improvementData.associatedCases ? improvementData.associatedCases.map(a => oid(a)) : null
        improvementData.areas = improvementData.areas ? improvementData.areas.map(a => oid(a)) : null
        improvementData.stage = 'analysis'
        improvementData.lastModifiedBy = userId ? oid(userId) : null,
        await createCase(improvementData)
        emit('reloadConfirmation')
        showSuccessMessage(i18n.t('message.improvement_created'))
      } catch (error) {
        console.log(error)
        showErrorMessage(i18n.t('message.improvement_error'))
        emit('isCreatingCase', false)
      }
    }

    return {
      options,
      showShortStatement,
      openCase,
      openDocument,

      // Improvement sidebar
      isImprovementHandlerSidebarActive,
      improvement,
      clearImprovementData,
      addImprovement,
    }
  },
}
</script>

<style scoped>
.rating-container {
  position: relative;
  display: inline-block;
}

.custom-tooltip {
  background-color: #82868b;
  color: white;
  padding: 8px 12px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  position: absolute;
  pointer-events: none;
}

.custom-tooltip::after {
  content: '';
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  border: 5px solid transparent;
  border-top-color: #82868b;
}

/* Asegurar que las estrellas sean interactivas para el hover */
/* .rating-container >>> .b-rating-star {
  cursor: pointer;
} */

/* Asegurar que el rating tenga el posicionamiento correcto */
.rating-container >>> .b-rating {
  position: relative;
}
</style>